import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { Button } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { generatePermanentBidReport } from "../api/report";
import { sortOnCaseSensitiveTexts } from "../util/commonUtils";
import { CANCEL_WARNING_MESSAGE_ACTION_TYPE, IAM_CONTRACT, TWU_CONTRACT } from "../util/constants.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE,
} from "../util/messageConstants";
import { validateWorkgroupList } from "../util/reportInputValidation";
import GenerateReportDownloadDialog from "./GenerateReportDownloadDialog";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice.js";

const useStyles = makeStyles((theme) => ({
  control: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filter: {
    width: "348px",
  },
  link: {
    display: "none",
  },
}));

const PermanentBidReport = (props) => {
  const classes = useStyles();
  const workgroupData = useSelector(selectWorkgroupData);
  // Filter Input State
  const [workgroupList, setWorkgroupList] = useState([]);
  const [workgroupListErrors, setWorkgroupListErrors] = useState("");
  // Generating Report State
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(false);
  const [isGenerateConfirmDialogOpen, setIsGenerateConfirmDialogOpen] = useState(false);
  const [isGenerateDownloadDialogOpen, setIsGenerateDownloadDialogOpen] = useState(false);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [isReportDownloadError, setIsReportDownloadError] = useState(false);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const linkRef = useRef();

  const permanentBidWorkgroups = useMemo(() => {
    //Since permanent bid is active for IAM and TWU, only filtering those workgroups.
    return Object.fromEntries(
      Object.entries(workgroupData).filter(([, value]) => [IAM_CONTRACT, TWU_CONTRACT].includes(value.unionContract))
    );
  }, [workgroupData]);

  // Generating Report Handlers
  const resetState = () => {
    setIsReportDownloading(false);
    setIsReportDownloadError(false);
    setResponseMessage("");
    setDownloadedFile(null);
  };

  const isFormValidationSuccess = () => {
    const workgroupListFormValidationErrors = validateWorkgroupList(workgroupList);
    setWorkgroupListErrors(workgroupListFormValidationErrors);

    return workgroupListFormValidationErrors === "";
  };

  const buildGenerateReportPayload = () => {
    return {
      station: props.station,
      workgroup: workgroupList,
    };
  };

  const handleGenerateReport = () => {
    resetState();
    setIsReportDownloading(true);
    generatePermanentBidReport(buildGenerateReportPayload())
      .then((res) => res.json())
      .then(async (result) => {
        if (result.message) {
          setResponseMessage(result.message);
        } else {
          setDownloadedFile(result);
        }
        setIsReportDownloading(false);
      })
      .catch((err) => {
        setIsReportDownloadError(true);
        setIsReportDownloading(false);
      });
  };

  const handleCancelButtonClicked = () => {
    setIsCancelConfirmDialogOpen(true);
  };

  const handleCancelDialogNoButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
    props.onResetState();
  };

  const handleGenerateButtonClicked = () => {
    if (isFormValidationSuccess()) {
      setIsGenerateConfirmDialogOpen(true);
    }
  };

  const handleGenerateDialogNoButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
  };

  const handleGenerateDialogYesButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
    setIsGenerateDownloadDialogOpen(true);
    handleGenerateReport();
  };

  const handleDownloadDialogClose = () => {
    setIsGenerateDownloadDialogOpen(false);
  };

  const handleDownloadDialogDownloadButtonClicked = () => {
    const a = linkRef.current;
    a.download = downloadedFile.fileName;
    a.href = downloadedFile.downloadUrl;
    a.click();
    a.href = "";
    setIsGenerateDownloadDialogOpen(false);
    resetState();
    props.onResetState();
  };

  const handleWorkgroupListChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = Object.keys(permanentBidWorkgroups);
      }
    }

    let inputErrors = validateWorkgroupList(newWorkgroupFilterSelectedList);
    setWorkgroupList(newWorkgroupFilterSelectedList);
    if (inputErrors === "") {
      setWorkgroupListErrors("");
    } else {
      setWorkgroupListErrors(inputErrors);
    }
  };

  const renderEligibleWorkgroupMenuItems = () => {
    return Object.keys(permanentBidWorkgroups)
      .slice()
      .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
      .map((workgroup) => {
        return (
          <MenuItem key={workgroup} value={workgroup}>
            <Checkbox color="primary" checked={workgroupList.indexOf(workgroup) > -1} />
            <ListItemText primary={workgroup} />
          </MenuItem>
        );
      });
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return workgroupList.length !== Object.keys(permanentBidWorkgroups).length && workgroupList.length !== 0;
  };

  const isWorkgroupSelectAllChecked = () => {
    return workgroupList.length === Object.keys(permanentBidWorkgroups).length;
  };

  return (
    <Grid item data-testid="permanent-bid-report">
      <Grid item xs={12}>
        <FormControl variant="outlined" className={classes.control} error={workgroupListErrors}>
          <InputLabel id="workgroup-label">Workgroup</InputLabel>
          <Select
            labelId="workgroup-label"
            id="workgroupFilterSelectElement"
            className={classes.filter}
            label="Workgroup"
            multiple={true}
            value={workgroupList}
            MenuProps={{ autoFocus: false }}
            onChange={handleWorkgroupListChange}
            renderValue={(selected) =>
              selected.length === Object.keys(permanentBidWorkgroups).length
                ? "ALL"
                : selected.length === 1
                ? selected[0]
                : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Workgroup</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={isWorkgroupSelectAllChecked()}
                indeterminate={isWorkgroupSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderEligibleWorkgroupMenuItems()}
          </Select>
          {workgroupListErrors !== "" && <FormHelperText error={true}>{workgroupListErrors}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid container item justifyContent="flex-end" spacing={2} xs={12} className={classes.buttonContainer}>
        <Grid item>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancelButtonClicked}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handleGenerateButtonClicked}
            size="large"
          >
            Generate
          </Button>
        </Grid>
      </Grid>
      {isCancelConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.SELECTIONS)}
          isDialogOpen={isCancelConfirmDialogOpen}
          handleNoButtonClicked={handleCancelDialogNoButtonClicked}
          handleYesButtonClicked={handleCancelDialogYesButtonClicked}
        />
      )}
      {isGenerateConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE}
          isDialogOpen={isGenerateConfirmDialogOpen}
          handleNoButtonClicked={handleGenerateDialogNoButtonClicked}
          handleYesButtonClicked={handleGenerateDialogYesButtonClicked}
        />
      )}
      {isGenerateDownloadDialogOpen && (
        <>
          <GenerateReportDownloadDialog
            isDownloading={isReportDownloading}
            isError={isReportDownloadError}
            isDialogOpen={isGenerateDownloadDialogOpen}
            onClose={handleDownloadDialogClose}
            onDownload={handleDownloadDialogDownloadButtonClicked}
            onRetry={handleGenerateReport}
            responseMessage={responseMessage}
          />
          <a className={classes.link} ref={linkRef} href="/">
            Download Report
          </a>
        </>
      )}
    </Grid>
  );
};

export default PermanentBidReport;
